import { useQuery } from "@tanstack/react-query";
import { LANG_ATTRIBUTE_TAG } from "@/constants/Language";
import api, { RestApiOptions } from "@/api";
import getApplicationEnvironment from "@/utils/getApplicationEnvironment";
import findDefaultLevelFromReadingOrGradeLevel from "@/view/utils/findDefaultLevelFromReadingOrGradeLevel";
import { User } from "@/types/models/User.interface";
import { LANGUAGE_CODE_MAP } from "@/constants/Language";
import ArticleLevel from "@/types/models/ArticleLevel.interface";

export const useRecommendedArticleLevelGet = (
  {
    content,
    contentId,
    language,
    user,
  }: {
    content: { data: { article_levels: ArticleLevel[] } };
    contentId: string;
    language: LANG_ATTRIBUTE_TAG;
    user: User;
  },
  skip = false,
) => {
  const userUniqueId = user?.unique_id;
  const classroomGradeLevels = user?.classroom_grade_levels ?? [];
  const averageClassroomGradeLevel =
    classroomGradeLevels.length > 0
      ? Math.round(
          classroomGradeLevels.reduce((sum, grade) => sum + grade, 0) /
            classroomGradeLevels.length,
        )
      : null;
  const userGradeLevel =
    averageClassroomGradeLevel ?? Math.round(user?.grade_level);
  const isMissingData = !contentId || !language || !userUniqueId;

  const isEnabled = !skip && !isMissingData;

  const { isDevEnv } = getApplicationEnvironment();

  const options: RestApiOptions = {
    basePath: isDevEnv ? "https://app.dev.newseladev.com/" : "/",
    timeout: 5000,
  };
  const resource = `seti/article-level-recommender/recommend-article-level`;

  const payload: Record<string, unknown> = {
    article_story_content_id: contentId,
    language,
    user_unique_id: userUniqueId,
  };

  if (userGradeLevel) {
    payload["academic_grade_level"] = userGradeLevel;
  }

  const query = useQuery({
    enabled: isEnabled,
    queryKey: ["contentId", contentId],
    queryFn: async () => api(options).post(resource, payload, {}),
    retry: false,
  });

  const hasQueryError = !query?.isLoading && query?.isError;

  if (!isEnabled || hasQueryError) {
    return {
      data: {
        recommendation: [
          findDefaultLevelFromReadingOrGradeLevel(
            user,
            content?.data?.article_levels,
            LANGUAGE_CODE_MAP[language],
            undefined,
          ),
        ],
      },
    };
  }
  return { loading: query?.isFetching, ...query };
};

export default useRecommendedArticleLevelGet;
